import React, { useCallback, useMemo } from 'react'
import { Col } from 'antd'
import { filter, isNil, map, toString } from 'lodash'

import { GridFormItem } from '../../../core/components/sections'
import { getFilterOptions, isBooleanTrue } from '../../../core/utils'

import { Select } from '../../../common/components/sections'
import {
  StyledFullWidthSearchInput,
  StyledPaddingBottomRow,
} from '../../../common/components/styled'
import {
  ALL_SELECT_FILTER_ITEM,
  BRAND_CURATION_STATUS_FILTER,
  EXCLUSION_STATUS,
  PAGINATION,
  TASK_DUPLICATE_FILTER,
  UNASSIGNED_USER_FILTER_ITEM,
} from '../../../common/constants'
import { useUsersQuery } from '../../../common/hooks'

import { checkIfQcAssignableUser } from '../../../account/utils'
import { BRAND_CURATION_TASK_STATUS } from '../../constants'
import { useBrandCurationTasksListFilters } from '../../hooks'

const BrandCurationTasksFilters = () => {
  const {
    taskStatus,
    isCurationDone,
    isQaDone,
    isQcDone,
    autoeatStatus,
    searchText,
    assignedCurator,
    assignedQa,
    assignedQc,
    duplicate,
    setFilters,
  } = useBrandCurationTasksListFilters()

  const { data: usersData } = useUsersQuery()

  const statusOptions = useMemo(
    () => [
      ALL_SELECT_FILTER_ITEM,
      ...getFilterOptions(
        filter(
          BRAND_CURATION_TASK_STATUS,
          taskStatus =>
            taskStatus.value !==
            BRAND_CURATION_TASK_STATUS.AUTOMATICALLY_RESOLVED.value,
        ),
        'label',
        'value',
      ),
    ],
    [],
  )

  const duplicateOptions = useMemo(
    () => [
      ALL_SELECT_FILTER_ITEM,
      ...getFilterOptions(TASK_DUPLICATE_FILTER, 'label', 'value'),
    ],
    [],
  )

  const autoeatStatusOptions = useMemo(
    () => [
      ALL_SELECT_FILTER_ITEM,
      ...getFilterOptions(EXCLUSION_STATUS, 'label', 'value'),
    ],
    [],
  )

  const brandCurationStatusOptions = useMemo(
    () => [
      ALL_SELECT_FILTER_ITEM,
      ...getFilterOptions(BRAND_CURATION_STATUS_FILTER, 'label', 'value'),
    ],
    [],
  )

  const brandCurationStatusValue = useMemo(() => {
    if (isBooleanTrue(isCurationDone)) {
      return BRAND_CURATION_STATUS_FILTER.CURATION_DONE.value
    }

    if (isBooleanTrue(isQaDone)) {
      return BRAND_CURATION_STATUS_FILTER.QA_DONE.value
    }

    if (isBooleanTrue(isQcDone)) {
      return BRAND_CURATION_STATUS_FILTER.QC_DONE.value
    }

    return ALL_SELECT_FILTER_ITEM.value
  }, [isCurationDone, isQaDone, isQcDone])

  const usersOptions = useMemo(
    () => [
      ALL_SELECT_FILTER_ITEM,
      UNASSIGNED_USER_FILTER_ITEM,
      ...map(usersData, ({ id, name }) => ({ label: name, value: id })),
    ],
    [usersData],
  )
  const qcAssignableUsersOptions = useMemo(
    () => [
      ALL_SELECT_FILTER_ITEM,
      UNASSIGNED_USER_FILTER_ITEM,
      ...map(filter(usersData, checkIfQcAssignableUser), ({ id, name }) => ({
        label: name,
        value: id,
      })),
    ],
    [usersData],
  )

  const selectedAssignedCurator = useMemo(
    () => (!isNil(assignedCurator) ? toString(assignedCurator) : null),
    [assignedCurator],
  )

  const selectedAssignedQa = useMemo(
    () => (!isNil(assignedQa) ? toString(assignedQa) : null),
    [assignedQa],
  )

  const selectedAssignedQc = useMemo(
    () => (!isNil(assignedQc) ? toString(assignedQc) : null),
    [assignedQc],
  )

  const handleSetSearchValue = useCallback(
    value => {
      setFilters({
        currentPage: PAGINATION.DEFAULT_PAGE,
        searchText: value,
      })
    },
    [setFilters],
  )

  const handleSetDuplicateValue = useCallback(
    value => {
      setFilters({
        currentPage: PAGINATION.DEFAULT_PAGE,
        duplicate: value,
      })
    },
    [setFilters],
  )

  const handleSetAssignedCuratorValue = useCallback(
    value => {
      setFilters({
        currentPage: PAGINATION.DEFAULT_PAGE,
        assignedCurator: value,
      })
    },
    [setFilters],
  )

  const handleSetAssignedQaValue = useCallback(
    value => {
      setFilters({
        currentPage: PAGINATION.DEFAULT_PAGE,
        assignedQa: value,
      })
    },
    [setFilters],
  )

  const handleSetAssignedQcValue = useCallback(
    value => {
      setFilters({
        currentPage: PAGINATION.DEFAULT_PAGE,
        assignedQc: value,
      })
    },
    [setFilters],
  )

  const handleSetStatusValue = useCallback(
    value => {
      setFilters({
        currentPage: PAGINATION.DEFAULT_PAGE,
        taskStatus: value,
      })
    },
    [setFilters],
  )

  const handleSetAutoeatStatusValue = useCallback(
    value => {
      setFilters({
        currentPage: PAGINATION.DEFAULT_PAGE,
        autoeatStatus: value,
      })
    },
    [setFilters],
  )

  const handleSetBrandCurationStatus = useCallback(
    value => {
      if (value === BRAND_CURATION_STATUS_FILTER.CURATION_DONE.value) {
        return setFilters({
          currentPage: PAGINATION.DEFAULT_PAGE,
          isCurationDone: true,
          isQaDone: null,
          isQcDone: null,
        })
      }

      if (value === BRAND_CURATION_STATUS_FILTER.QA_DONE.value) {
        return setFilters({
          currentPage: PAGINATION.DEFAULT_PAGE,
          isQaDone: true,
          isCurationDone: null,
          isQcDone: null,
        })
      }

      if (value === BRAND_CURATION_STATUS_FILTER.QC_DONE.value) {
        return setFilters({
          currentPage: PAGINATION.DEFAULT_PAGE,
          isQaDone: null,
          isCurationDone: null,
          isQcDone: true,
        })
      }

      return setFilters({
        currentPage: PAGINATION.DEFAULT_PAGE,
        isCurationDone: null,
        isQaDone: null,
        isQcDone: null,
      })
    },
    [setFilters],
  )

  return (
    <>
      <StyledPaddingBottomRow gutter={16}>
        <Col span={6}>
          <StyledFullWidthSearchInput
            placeholder="Search Brands"
            defaultValue={searchText}
            onSearch={handleSetSearchValue}
          />
        </Col>
      </StyledPaddingBottomRow>
      <StyledPaddingBottomRow gutter={16}>
        <GridFormItem span={3} label="Assigned Curator">
          <Select
            allowClear
            showSearch
            options={usersOptions}
            value={selectedAssignedCurator}
            onChange={handleSetAssignedCuratorValue}
          />
        </GridFormItem>
        <GridFormItem span={3} label="Assigned Qa">
          <Select
            allowClear
            showSearch
            options={usersOptions}
            value={selectedAssignedQa}
            onChange={handleSetAssignedQaValue}
          />
        </GridFormItem>
        <GridFormItem span={3} label="Assigned Qc">
          <Select
            allowClear
            showSearch
            options={qcAssignableUsersOptions}
            value={selectedAssignedQc}
            onChange={handleSetAssignedQcValue}
          />
        </GridFormItem>
        <GridFormItem span={3} label="Status">
          <Select
            allowClear
            showSearch
            options={statusOptions}
            value={taskStatus}
            onChange={handleSetStatusValue}
          />
        </GridFormItem>
        <GridFormItem span={3} label="Brand Curation Status">
          <Select
            allowClear
            showSearch
            placeholder="Select Brand Curation Status"
            options={brandCurationStatusOptions}
            value={brandCurationStatusValue}
            onChange={handleSetBrandCurationStatus}
          />
        </GridFormItem>
        <GridFormItem span={3} label="Autoeat Status">
          <Select
            allowClear
            showSearch
            options={autoeatStatusOptions}
            value={autoeatStatus}
            onChange={handleSetAutoeatStatusValue}
          />
        </GridFormItem>
        <GridFormItem span={3} label="Duplicate">
          <Select
            allowClear
            showSearch
            options={duplicateOptions}
            value={duplicate}
            onChange={handleSetDuplicateValue}
          />
        </GridFormItem>
      </StyledPaddingBottomRow>
    </>
  )
}

export default BrandCurationTasksFilters
